import {
  Box,
  Button,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup"
import apiHelper from "../../../util/ApiHelper";
import { useLoading } from "../../../util/LoadingProvider";

const schema = yup.object().shape({
  hard_version_iphone: yup.string(),
  soft_version_iphone: yup.string(),
  link_iphone: yup
        .string()
        .nullable()
        .optional()
        .matches(RegExp(/http:\/\/|https:\/\//g), {
          message: "Missing http:// or https://",
          excludeEmptyString: true,
        }),
  hard_version_android: yup.string(),
  soft_version_android: yup.string(),
  link_android: yup
  .string()
  .nullable()
  .optional()
  .matches(RegExp(/http:\/\/|https:\/\//g), {
    message: "Missing http:// or https://",
    excludeEmptyString: true,
  }),
});

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: "40px",
  },
  innerBox: {
    background: "#ffffff",
    marginBottom: theme.spacing(4),
    "& *": {
      fontSize: "13px",
      lineHeight: "15px",
    },
  },
  sectionBox: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },
  sectionTitle: {
    fontWeight: "300",
    fontSize: "13px",
    lineHeight: "15px",
    color: "#333333",
  },
  itemTitle: {
    fontWeight: "bold",
    fontSize: "12px",
    lineHeight: "14px",
    color: "#333333",
  },
  itemValue: {
    fontWeight: "normal",
    fontSize: "14px",
    lineHeight: "16px",
    color: "#333333",
  },
  input: {
    width: "100%",
    "& button": {
      fontSize: "13px",
      lineHeight: "15px",
      width: "25%",
    },
  },
  buttonGroup: {
    textAlign: "right",
    marginTop: "-20px",
    "& button": {
      color: "#306F9D",
      fontSize: "13px",
      textTransform: "inherit",
    },
  },
  customLabel: {
    margin: "0 !important",
    "& label": {
      color: "black",
      fontWeight: 700,
      fontSize: "16px",
    },
    "& p": {
      fontSize: "12px",
    },
  },
}));

const defaultFormValues = {
  localizations: {
    en: {
      address: "",
      tel: "",
      fax: "",
      email: "",
      website: "",
      office_hours: "",
      title: "",
    },
    tc: {
      address: "",
      tel: "",
      fax: "",
      email: "",
      website: "",
      office_hours: "",
      title: "",
    },
  },
};




function ContactUsPage({ disabled, ...props }) {
  const classes = useStyles();
  const { match } = props;
  const { id } = match.params;
  const [defaultValues, setDefaultValues] = React.useState(defaultFormValues);
  const [resultValues, setResultValues] = React.useState(null);
  const { setLoading } = useLoading();

  const {
    control,
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset,
    formState,
  } = useForm({
    defaultValues: React.useMemo(() => defaultValues, [defaultValues]),
    resolver: yupResolver(schema),
  });

  function newResultValues(key, value, lang){
    var newResultValues = resultValues;
    resultValues.forEach((resultValue, i) => {
      if(resultValue.optionKey == key){
        newResultValues[i].localizations[lang].optionValue = value;
      }
    });
    setResultValues(newResultValues);
  }

  function updateResultValues(data){
    for (const [dataKey, dataValue] of Object.entries(data)) {
      if(dataKey == "localizations"){
        for (const [localizationsKey, localizationsValue] of Object.entries(dataValue)) {
          for (const [key, value] of Object.entries(localizationsValue)) {
            newResultValues(key,value,localizationsKey)
          }
        }
      }else{
        newResultValues(dataKey,dataValue,"en")
      }
    };
  }

  const onSubmit = async (value) => {
    setLoading(true);
    // purge date and time
    const { date, time, ...data } = value;
    try {
        updateResultValues(data);
        await apiHelper.hkiffApp.updateHkiffApp("contact_us", resultValues);
        toast.dark("Contact Us is updated.");
    } catch (e) {
      console.log(e);
    }
    setLoading(false);
  };

  const getAppVersion = React.useCallback(async () => {
    try {
      setLoading(true);
      const result = await apiHelper.hkiffApp.getHkiffApp("contact_us");
      // initialize date and time
      const data = result.data;
      var newDefaultFormValues = defaultFormValues;
      var obj = data.reduce(function ( total, current ) {
        if(current.localizations.tc && newDefaultFormValues.localizations.tc[ current.optionKey ] != undefined){
          newDefaultFormValues["localizations"]["tc"][current.optionKey] = current.localizations.tc.optionValue;
          newDefaultFormValues["localizations"]["en"][current.optionKey] = current.localizations.en.optionValue;
        }else{
          newDefaultFormValues[ current.optionKey ] = current.localizations.en.optionValue;
        }
    }, {});
      setResultValues(data);
      setDefaultValues(newDefaultFormValues);
      reset(newDefaultFormValues);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  React.useEffect(() => {
    getAppVersion();
  }, [getAppVersion, id]);

  return (
    <Grid container xs={12} md={8}>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: "80%" }}>
        <Typography variant="h4" gutterBottom>
          Contact Us
      </Typography>
        <Box display="flex" flexDirection="column" className={classes.root}>
          <Box fontWeight={900} fontSize={13} ml={1} mb={3} width="100%">
            <Typography variant="p">Content (English)</Typography>
          </Box>
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Page Title"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.address"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Address"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.tel"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Telephone"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.fax"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Fax"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Email"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.website"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Website"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.en.office_hours"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Office Hours"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Box fontWeight={900} fontSize={13} ml={1} mb={3} mt={4} width="100%">
            <Typography variant="p">Content (Traditional Chinese)</Typography>
          </Box>
          <Grid container xs={12} direction="row" spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.title"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Page Title"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.address"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="地址"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.tel"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="電話"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.fax"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="傳真"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.email"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Email"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.website"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Website"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="localizations.tc.office_hours"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    label="Office Hours"
                    variant="outlined"
                    margin="dense"
                    className={classes.textInput}
                    fullWidth
                    {...field}
                    error={!!error}
                    helperText={error ? error.message : ""}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        {disabled || (
        <Grid container xs={12} direction="row" spacing={5}>
          <Grid item xs={6} md={4} lg={4} style={{ mt: 2 }}>
            <Button
              className={classes.button}
              type="submit"
              variant="contained"
              color="primary"
            >
              Save Changes
            </Button>
          </Grid>
        </Grid>
        )}

      </form>
    </Grid>
  );
}

export default ContactUsPage;
